import { RedirectFunction, RouterState } from 'react-router';
import type { AnyAction, Store, ThunkDispatch } from '@reduxjs/toolkit';

import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';

import { intercept401 } from 'Redux/utils';
import { ApologyActions } from 'Redux/thunks/apology.thunks';

import { sfetchInit } from './sfetch';

import { UserAPI } from '../api/user.api';

import { SubActions, makeRoleCheckerForCurrentSub } from 'Redux/thunks/subscriptions.thunks';
import { PipeActions } from 'Redux/thunks/pipes.thunks';
import { DatasetActions } from 'Redux/thunks/datasets.thunks';
import { SystemActions } from 'Redux/thunks/systems.thunks';
import { UserProfileActions } from 'Redux/thunks/user-profile.thunks';
import { lastViewedTabUpdated, NavId } from 'Redux/thunks/navigation.thunks';
import { userLoggedIn } from 'Redux/thunks/auth.thunks';

import type { AnyFunction } from '../types/common.types';
import type { RootState } from '../types/state.types';

let store: Store;

/**
 * Shortcut function
 * @return {object} The full Redux state
 */
function getState() {
  return store.getState();
}

/**
 * Utility function to do something on change of a store key
 * @param  {Function} valueGetter Retrives the value to watch from the store
 * @param  {Function} callback    Called when changes happen
 */
export function onKeyChange(valueGetter: Function, callback: Function) {
  let currentValue: any;

  function handleChange() {
    const previousValue = currentValue;
    currentValue = valueGetter();

    if (previousValue !== currentValue) {
      callback(currentValue, previousValue);
    }
  }

  store.subscribe(handleChange);
}

export function requireAuth(
  _nextState: RouterState,
  replace: RedirectFunction,
  callback?: AnyFunction
) {
  if (!getState().user.loggedIn) {
    // Not logged in! Please wait; checking with HQ…
    UserAPI.get(getState().globals.portalUrl)
      .then(() => {
        // looks like we already had a working session
        store.dispatch(userLoggedIn());

        callback?.();
      })
      .catch((error) => {
        if (!intercept401(error, getState, store.dispatch)) {
          replace('/server-error');

          callback?.();
        }
      });
  } else {
    // Logged in; please proceed, madam.
    callback?.();
  }
}

/*
 * Support another preload data callback function.
 * Returns `Promise`.
 */
export const asyncRequireAuth = async (args: {
  nextState: RouterState;
  replace: RedirectFunction;
}) => {
  const { replace } = args;

  if (!getState().user.loggedIn) {
    // Not logged in! Please wait; checking with HQ…
    return UserAPI.get(getState().globals.portalUrl)
      .then(() => {
        // looks like we already had a working session
        store.dispatch(userLoggedIn());

        return Promise.resolve();
      })
      .catch((error) => {
        if (!intercept401(error, getState, store.dispatch)) {
          replace('/server-error');

          return Promise.reject();
        }
      });
  } else {
    return Promise.resolve();
  }
};

export function setLastViewedTab(navId: NavId, value: string, callback?: () => void) {
  store.dispatch(lastViewedTabUpdated({ navId, value }));

  callback?.();
}

export function redirectFromIndexRoute(
  navId: string,
  defaultRoute: string,
  baseUrl: string,
  replace: RedirectFunction,
  callback?: () => void
) {
  const state = store.getState();
  const lastViewed = get(state, `navigation.lastVisitedTab.${navId}`);

  const to = lastViewed ? `${baseUrl}/${lastViewed}` : `${baseUrl}/${defaultRoute}`;

  replace(to);

  if (callback) {
    callback();
  }
}

export function requirePortalAdmin(
  redirectRoute: string,
  replace: RedirectFunction,
  callback: Function | undefined
) {
  const state = getState();
  if (state.user.isPortalAdmin) {
    if (callback) callback();
  } else {
    replace(redirectRoute);
    if (callback) callback();
  }
}

export function requireGroupMembership(
  groupName: string,
  redirectRoute: string,
  replace: RedirectFunction,
  callback: Function | undefined
) {
  const state = getState();
  const roleChecker = makeRoleCheckerForCurrentSub(state);

  if (roleChecker(groupName)) {
    if (callback) callback();
  } else {
    replace(redirectRoute);
    if (callback) callback();
  }
}

function wireupUserLogin() {
  // Load user profile when user is logged in
  let versionCheck: ReturnType<typeof setTimeout>;
  onKeyChange(
    () => getState().user.loggedIn,
    (loggedIn: boolean) => {
      if (loggedIn === true) {
        (store.dispatch as any)(UserProfileActions.load());
        (store.dispatch as any)(SubActions.loadAll());
        // Checks for new version and alert user
        versionCheck = setInterval(() => {
          (store.dispatch as any)(ApologyActions.checkVersion());
        }, 5 * 60000);
      } else {
        clearTimeout(versionCheck);
      }
    }
  );
}

function wireupConnected() {
  // Load node data when subscription is connected
  // TODO: should be able clean up BareSubscriptionPage.componentDidMount
  onKeyChange(
    () => getState().subscription.connected,
    (connected: boolean) => {
      if (connected === true) {
        (store.dispatch as any)(PipeActions.loadAll(true));
        (store.dispatch as any)(SystemActions.loadAll());
        (store.dispatch as any)(DatasetActions.loadAll());
        (store.dispatch as any)(DatasetActions.loadAllInternals());

        // TODO: load internal pipes/systems and datasets
      }
    }
  );
}

function wireupStatusPage() {
  onKeyChange(
    () => {
      const status = getState().globals.status;
      if (status && !isUndefined(status.is_itest_mode)) {
        return status.is_itest_mode;
      }
    },
    (isItestMode: boolean | undefined) => {
      if (isItestMode === false) {
        const statusPageScript = document.createElement('script');
        statusPageScript.setAttribute('src', 'https://lbct3259brkc.statuspage.io/embed/script.js');
        document.body.appendChild(statusPageScript);
      }
    }
  );
}

export const wireUpInternals = (
  injectStore: Store<RootState, AnyAction> & {
    dispatch: ThunkDispatch<RootState, unknown, AnyAction>;
  }
) => {
  store = injectStore;

  wireupUserLogin();
  wireupConnected();
  sfetchInit(store);
  wireupStatusPage();

  (store.dispatch as any)(ApologyActions.checkStatus());
};
